import React, { useEffect, useRef, useState } from "react";
import {
  useGetVQAActivity,
  useGetAssesmentRequestDetails,
  useDocumentFile,
  useDocumentAllFiles,
  useGetRFIsDetails,
  useGetClientSecrets,
  useGetRFIActivity,
  useAcceptVendorQuestionnaire,
  useExportQuestionnaireExcel,
  useDownloadExportFile,
  useDownloadExportDocs,
} from "../../custom-hooks/queries";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { Icon } from "@iconify/react/dist/iconify.js";
import LOGO from "../../assets/images/diligentLogoShortavif.avif";
import SkeletonLoader from "../../components/SkeletonLoader";

import Skeleton from "react-loading-skeleton";
import {
  dateFormat,
  calculateDaysAgo,
  useUserRole,
  isDiligentAdminUserRole,
} from "../../utils/helperMethods";
import StatusProgressBar from "../../components/ProgressBar";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import Pagination from "../../components/CustomPagination";
import StatusForDrawer from "../../components/StatusForDrawer";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ReportContainer } from "../../components/ReportContainer";
import DataLoading from "../../components/Skeletons/DataLoader";
import { ResponseTab } from "./ResponstTab";
import CustomDropdown from "../../components/RFIsComponents/Common/CustomDropdown";
import { StatusBadges } from "../../components/StatusBadges";
import { Button } from "@headlessui/react";
import AdminDetailsLoader from "../../components/Skeletons/adminDetailsLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { VendorQuestionnaireAccept } from "../../components/RFIsComponents/VendorQuestionnaireAccept";
import { use } from "react";
import ExportMenu from "../../components/Menus/ExportMenu";
import moment from "moment";
import Notification from "../../components/Notification";
import DownloadNotifications from "../../components/Notifications/DownloadNotifications";
import { file } from "jszip";
import { getLocationFromProvider } from "../../utils/addressUtils";

export const RFIDetails = () => {
  const user = useUserRole();
  const diligentAdmin = isDiligentAdminUserRole(user);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { datePassed } = location.state || {};
  const [downloadNotificationOpen, setDownloadNotificationOpen] =
    useState(false);
  const [fileType, setFileType] = useState("");
  const [downloadFileName, setDownloadFileName] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");

  const { data: clientSecrets } = useGetClientSecrets();
  const [message, setMessage] = useState("");
  const [show, setShow] = useState(false);

  const { data: rfisDetails, isLoading, refetch } = useGetRFIsDetails(id);
  const { data: activity, refetch: activityRefetch } = useGetRFIActivity(
    rfisDetails?.sponsorRfiResponseId
  );

  const [comment, setComment] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [searchParams] = useSearchParams();
  const responses = searchParams.get("page");
  const { mutate: exportExcel } = useExportQuestionnaireExcel();
  const {
    mutate: exportToExcel,
    data: exportData,
    isError: exportExcelError,
  } = useDownloadExportFile();

  const {
    mutate: exportToDocs,
    data: exportDocsData,
    isError: exportDocsError,
  } = useDownloadExportDocs();

  const handleExportExcel = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setFileType(`rfifiles-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
      },
    });
  };

  const handleAllDocuments = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "alldocuments",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setFileType(`rfidocs-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
      },
    });
  };

  const lastZipDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportDocsData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastZipDownloadedRef.current === downloadLink) return;
    lastZipDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = "document";

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            fileName = match[1].split(".")[0];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${rfisDetails?.name || fileName}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("ZIP download failed:", error);
      });
  }, [exportDocsData?.download_link, exportExcelError]);

  const lastExportDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastExportDownloadedRef.current === downloadLink) return;
    lastExportDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        const contentType = res.headers.get("Content-Type");

        let fileName = "document";
        let fileExtension = "xlsx"; // default

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || "xlsx";
          }
        } else if (downloadLink.includes("?")) {
          const match = downloadLink.match(/\.([a-zA-Z0-9]+)(\?|$)/);
          if (match?.[1]) {
            fileExtension = match[1];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${rfisDetails?.name || fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("File download failed:", error);
      });
  }, [exportData?.download_link, exportExcelError]);

  const [isAccepting, setIsLoading] = useState(false);

  // Set active tab based on URL query parameter
  useEffect(() => {
    if (responses === "responses") {
      setActiveTab("responses");
    }
  }, [responses]);

  // Handler for closing the modal
  const handleModalClose = () => {
    navigate("/RequestAssesmentList");
  };

  // const {
  //   mutate,
  //   data: files,
  //   isError: isFileError,
  //   error: errorMessageFile,
  // } = useDocumentFile();

  // const {
  //   mutate: allFilesDownload,
  //   data: allFiles,
  //   isError: allFilesError,
  // } = useDocumentAllFiles();

  // const handleDownload = (id) => {
  //   mutate(id, {
  //     onSuccess: () => {
  //       setShow(true);
  //       setMessage("Downloaded Successfully");
  //     },
  //     onError: () => {
  //       setShow(true);
  //       setMessage("Download Failed");
  //     },
  //   });
  // };

  // const handleDownloadAll = async (ids) => {
  //   allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  // };

  // Filter documents based on whether any sponsor has cancelled

  // Tab switching handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const [updateNotificationOpen, setUpdateNotificationOpen] = useState(false);

  const [iframeUrl, setIframeUrl] = useState("");

  useEffect(() => {
    if (!rfisDetails) return;

    const url =
      user === "Provider User"
        ? `/questionnaires/${rfisDetails.purchase_vendor_questionnaire_id}?hideNavigation=true`
        : `/due-diligence/${rfisDetails.purchaseVendorId}/questionnaires/${rfisDetails.questionnaire_id}?hideNavigation=true`;

    setIframeUrl(url);
  }, [rfisDetails, user]);

  const { mutate: acceptQuestionnaire, isError } =
    useAcceptVendorQuestionnaire();

  const handleSubmit = () => {
    setIsLoading(true);
    acceptQuestionnaire(
      {
        questionnaire_id: rfisDetails?.questionnaire_id,
        data: { email: rfisDetails?.vendor_contact_email },
      },
      {
        onSuccess: () => {
          setMessage("Questionnaire accepted successfully");
          setUpdateNotificationOpen(true);
          refetch();
          setTimeout(() => {
            setIsLoading(false);

            navigate(
              `/RFI/${rfisDetails?.questionnaire_id}/Details?page=responses`
            );
          }, 1000);
        },
        onError: (error) => {
          setIsLoading(false);
          setMessage("Error accepting questionnaire");
          setUpdateNotificationOpen(true);
        },
      }
    );
  };

  return (
    <div className="container">
      <div className="p-7 border-y border-gray-300 bg-slate-50 mb-5">
        <div className="flex items-center text-sm">
          <h1
            className="text-gray-500 cursor-pointer font-semibold"
            onClick={() =>
              navigate(
                user === "Provider User" ? "/RFI/Requests" : "/RFI/History"
              )
            }
          >
            My VQQs
          </h1>
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        </div>
        <div className="mt-5 space-y-2 flex  flex-col justify-start items-start md:justify-between md:items-center md:flex-row">
          <div className="flex items-center">
            <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
              {/* <CheckBadgeIcon className="h-9 w-9 text-red-500" /> */}
              <img src={LOGO} alt="Logo" className="h-10 w-10" />
            </span>

            <span>
              <p className="text-md font-semibold">{rfisDetails?.vendorName}</p>
              <p className="text-sm text-gray-500">{rfisDetails?.name}</p>
              <p className="text-sm text-gray-500">
                {dateFormat(rfisDetails?.requestDate)}
              </p>

              <p className="text-sm">
                <span className="font-semibold">
                  {rfisDetails?.RequestDate}
                </span>
              </p>
            </span>
          </div>

          <div>
            {" "}
            {!rfisDetails?.questionnaire_accepted &&
              user === "Provider User" && (
                <CustomButton
                  color={isAccepting ? "bg-gray-300" : "bg-indigo-600"}
                  size="text-base"
                  weight="font-normal"
                  height="h-9"
                  width="w-48"
                  textColor="text-white"
                  border={"border border-gray-300"}
                  text={isAccepting ? "Accepting..." : "Accept Questionnaire"}
                  // icon={
                  //   <FontAwesomeIcon
                  //     icon={faFileExcel}
                  //     className="w-[16px] h-[16px] text-white-700"
                  //   />
                  // }
                  onClick={() => {
                    handleSubmit();
                  }}
                  borderRadius="0.375rem"
                  disabled={isAccepting}
                />
              )}
          </div>
        </div>

        <div className="mt-5 flex flex-col md:flex-row md:justify-between items-start md:items-center space-y-2"></div>
      </div>{" "}
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 px-5">
        <nav className="-mb-px flex items-center justify-between">
          <div className="flex justify-between items-center w-full">
            <div>
              <button
                onClick={() => handleTabChange("overview")}
                className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              {((user === "Sponsor User" &&
                rfisDetails?.status !== "Sent" &&
                rfisDetails?.status !== "In Progress") ||
                (user === "Provider User" &&
                  rfisDetails?.questionnaire_accepted)) && (
                <button
                  onClick={() => handleTabChange("responses")}
                  className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === "responses"
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Responses
                </button>
              )}
            </div>
            <div className="flex flex-row items-center space-x-2">
              {rfisDetails?.status === "Complete" && (
                <ExportMenu
                  exportExcel={handleExportExcel}
                  exportDocs={handleAllDocuments}
                  notification={downloadNotificationOpen}
                  setNotification={setDownloadNotificationOpen}
                  fileType={fileType}
                  details={rfisDetails}
                  downloadFileName={downloadFileName}
                />
              )}
            </div>
          </div>
          {/* <span className="sm:mr-0 lg:mr-[12.8rem]">
            <CustomDropdown />
          </span> */}
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === "overview" &&
        (isLoading ? (
          <div className="w-full md:w-[70%] border-r border-gray-300 px-3 md:mr-5 lg:mr-8  ">
            <AdminDetailsLoader />
          </div>
        ) : (
          <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
            <div className="w-full md:w-[90%] border-r border-gray-300 px-3 mr-8">
              <div className="divide-y divide-gray-300">
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">Status </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <StatusBadges statusName={rfisDetails?.status} />
                  </p>
                </div>

                {diligentAdmin && rfisDetails?.status === "Archived" ? (
                  <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                    <h2 className="text-sm font-medium text-gray-900">
                      Archive Comment
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 sm:col-span-2 sm:mt-0 italic">
                      {(rfisDetails?.archiveComment ?? "").trim() || "—"}
                    </p>
                  </div>
                ) : null}

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Template
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {rfisDetails?.name}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">Type</h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span
                      className={`inline-flex items-center rounded-md ${
                        rfisDetails?.services.includes("Diligent")
                          ? "bg-purple-50"
                          : "bg-gray-50"
                      } px-2 py-1 text-xs font-medium ${
                        rfisDetails?.services.includes("Diligent")
                          ? "text-purple-700"
                          : "text-gray-700"
                      } ring-1 ${
                        rfisDetails?.services.includes("Diligent")
                          ? "ring-purple-600/20"
                          : "ring-gray-600/20"
                      } ring-inset`}
                    >
                      {rfisDetails?.services}
                    </span>
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Location
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {getLocationFromProvider({
                      address: rfisDetails?.location,
                      city: rfisDetails?.locationCity,
                      state: rfisDetails?.locationState,
                      country: rfisDetails?.locationCountry
                    }, { short: false, abbvState: true }) || rfisDetails?.location}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Contact
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <Icon
                        icon="fa-solid:address-book"
                        className="w-4 h-4 text-black-600"
                      />
                      {rfisDetails?.sponsor_contact_email}
                    </span>
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Vendor Contact
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <Icon
                        icon="fa-solid:address-book"
                        className="w-4 h-4 text-black-600"
                      />
                      {rfisDetails?.vendor_contact_email}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Request Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {dateFormat(rfisDetails?.requestDate)}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Release Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {rfisDetails?.releaseDate
                        ? dateFormat(rfisDetails?.releaseDate)
                        : "-"}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Additional Info
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {rfisDetails?.additionalInfo}
                  </p>
                </div>
              </div>
            </div>

            {/* Right column: Reports and Assessment Activity */}
            <div className="w-full md:w-[30%] mt-10 md:mt-0 mb-20">
              <div></div>
              <div className="mt-10 md:mt-6">
                <h2 className="text-sm font-semibold text-gray-900 ml-2">
                  VQQ Activity
                </h2>
                <ul className="mt-6 space-y-6 h-80 px-2">
                  {activity
                    ?.slice()
                    ?.sort((a, b) => {
                      // First sort by date (descending)
                      const dateA = new Date(a.activity_done_on);
                      const dateB = new Date(b.activity_done_on);
                      const dateDiff = dateB - dateA;

                      // If dates are the same (or very close), sort by activity sequence
                      if (Math.abs(dateDiff) < 1000) {
                        // Less than 1 second difference
                        const getActivityOrder = (description) => {
                          if (description.includes("Sent Questionnaire"))
                            return 1;
                          if (
                            description.includes(
                              "Status changed from Sent to In Progress"
                            )
                          )
                            return 2;
                          if (
                            description.includes(
                              "Status changed from In Progress to In Review"
                            )
                          )
                            return 3;
                          if (
                            description.includes(
                              "Status changed from In Review to In Remediation"
                            )
                          )
                            return 4;
                          if (
                            description.includes(
                              "Status changed from In Remediation to Completed"
                            )
                          )
                            return 5;
                          if (
                            description.includes("reaffirmed as In Remediation")
                          )
                            return 6;
                          if (
                            description.includes(
                              "Status changed from In Review to Complete"
                            )
                          )
                            return 7;
                          return 0; // Unknown activities go first
                        };

                        return (
                          getActivityOrder(b.activity_description) -
                          getActivityOrder(a.activity_description)
                        );
                      }

                      return dateDiff;
                    })
                    ?.map((item, index, array) => {
                      // Get the latest activity (most recent date)
                      const isLatest =
                        item?.activity_done_on ===
                        array.reduce((latest, current) =>
                          new Date(current?.activity_done_on) >=
                          new Date(latest?.activity_done_on)
                            ? current
                            : latest
                        )?.activity_done_on;

                      // Format date as 'Month DD, YYYY'
                      const formattedDate = item?.activity_done_on
                        ? new Date(item.activity_done_on).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : "-";

                      return (
                        <li key={index} className="relative flex gap-x-4">
                          {/* Line connecting items */}
                          <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                            <div className="w-px bg-gray-200"></div>
                          </div>

                          {/* Circle or Profile Picture */}
                          <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                            {item?.activity_comments && !isLatest ? (
                              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  {item?.activity_done_by
                                    ?.charAt(0)
                                    .toUpperCase() || "A"}
                                </span>
                              </div>
                            ) : (
                              <div className="absolute right-0 bottom-0 text-xs text-green-500">
                                <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                              </div>
                            )}
                          </div>

                          {/* Activity Description and Comment */}
                          <div className="flex-[1_1_0%]">
                            <p className="py-0.5 text-xs text-gray-500">
                              <span className="font-medium text-gray-900">
                                {!item?.activity_comments &&
                                  item?.activity_done_by}
                              </span>{" "}
                              {item?.activity_description}
                            </p>
                            {item?.activity_comments && (
                              <div className="mt-2 rounded-md border border-gray-300 p-3 text-sm text-gray-800 shadow-sm w-[110%]">
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-900 text-xs">
                                    {item?.activity_done_by}{" "}
                                    <span className="py-0.5 text-xs leading-5 text-gray-500 font-normal">
                                      commented
                                    </span>
                                  </span>
                                  <time
                                    dateTime={item?.activity_done_on}
                                    className="flex-none py-0.5 text-xs text-gray-500"
                                  >
                                    {formattedDate}
                                  </time>
                                </div>
                                <div className="text-gray-500 text-sm mt-2">
                                  {item?.activity_comments}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Activity Time */}
                          <time
                            dateTime={item?.activity_done_on}
                            className="flex-none py-0.5 text-xs text-gray-500"
                          >
                            {!item?.activity_comments && formattedDate}
                          </time>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          </div>
        ))}
      {/* Response Tab Content (Empty) */}
      {(activeTab === "responses" ||
        (responses === "responses" && activeTab !== "overview")) && (
        <ResponseTab
          activeTab={activeTab}
          clientSecrets={clientSecrets}
          route={iframeUrl}
          heading={
            user === "Provider User" ? "View Questionnaire" : "View Responses"
          }
          desc={user === "Provider User" ? "questionnaire" : "responses"}
        />
      )}
      <Notification
        show={updateNotificationOpen}
        setShow={setUpdateNotificationOpen}
        status={isError ? "error" : "success"}
        errorMessage={message}
        // title={isError ? "Error" : status}
        // error={isError || status === "Action Not Allowed!" ? true : false}
      />{" "}
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        onClick={handleModalClose}
        is_requested={isRequested}
        title={modalTitle}
        description={modalDescription}
        textBtn="Assesments Request List"
        vqaDetailsRefetch={handleModalClose}
      />
    </div>
  );
};

export default RFIDetails;
